import { Card, CardBody, RadioGroup, ScrollShadow, Spinner, Tab, Tabs } from "@nextui-org/react";
import { useCallback } from "react";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import useTableProduction from "../../../../hooks/report/production/useTableProduction";
import TableHistoryProduction from "../../../../components/fragments/admin/report/production/TableHistoryProduction";
import CardRadioButton from "../../../../components/fragments/admin/report/CardRadioButton.js";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import { filteredMaterialDataReportProduction, hasValidRole } from "../../../../services/helpers";
import ModalAddProductionMaterial from "../../../../components/fragments/admin/report/production/ModalAddProductionMaterial";
import RolesAccess from "../../../../enums/roles";
import useAuth from "../../../../hooks/useAuth";
import TableReportPreview from "../../../../components/fragments/admin/report/preview/TableReportPreview";
import { deleteWithFormFetcherTest } from "../../../../services/api";

// =====================================================================
// ENDPOINT
//
// Hanya aksi hapus. Bekerja di level BAHAN/PRODUK, bukan per baris: satu
// bahan punya satu baris pelaporan per bulan, jadi menghapus lewat loop dari
// frontend berisiko gagal separuh jalan dan meninggalkan data setengah
// terhapus. Lihat backend-additions.ts untuk implementasinya.
// =====================================================================
const deleteMaterialApi = (payload) => deleteWithFormFetcherTest("/api/pelaporan-b3-dihasilkan/material", payload);

export default function IndexPage() {
  const { roles } = useAuth();
  const {
    dataFetch: { data, isLoading, mutate, dataCompany, isLoadingCompany, dataPeriods, isLoadingPeriods },
    state: { selectedCompany, setSelectedCompany, selectedMaterial, setSelectedMaterial, selectedPeriod, setSelectedPeriod },
    columnsTableActivePeriod,
  } = useTableProduction();

  const isAdminOrPengelola = hasValidRole(roles, [RolesAccess.PENGELOLA, RolesAccess.SUPER_ADMIN]);

  const materialData = filteredMaterialDataReportProduction(data, isLoading);

  /**
   * Ambil baris pelaporan milik satu bahan/produk.
   *
   * `materialId` bisa berupa dataBahanB3Id ATAU namaProduk. Lihat
   * filteredMaterialDataReportProduction: baris dengan dataBahanB3Id null
   * dikelompokkan memakai namaProduk sebagai id kartu.
   *
   * Logika ini sebelumnya ditulis inline di filteredData; sekarang dipakai
   * bersama oleh tabel dan aksi hapus supaya keduanya tidak mungkin memberi
   * hasil yang berbeda.
   */
  const getRowsForMaterial = useCallback(
    (materialId) => {
      const rows = data?.data ?? [];
      if (!materialId) return [];
      const byBahanB3 = rows.filter((item) => item.dataBahanB3Id === materialId);
      return byBahanB3.length > 0 ? byBahanB3 : rows.filter((item) => item.namaProduk === materialId);
    },
    [data?.data],
  );

  const filteredData = selectedMaterial && !isLoading && data ? getRowsForMaterial(selectedMaterial) : [];

  // Hapus seluruh baris pelaporan bahan/produk tsb, lalu bersihkan pilihan aktif.
  const handleDeleteMaterial = useCallback(
    async (material) => {
      const [firstRow] = getRowsForMaterial(material?.id);
      if (!firstRow) throw new Error("Tidak ada baris pelaporan untuk bahan ini");

      // Baris pelaporan adalah satu-satunya tempat yang tahu apakah kartu ini
      // mewakili bahan B3 terdaftar atau produk non-B3 bernama bebas.
      const payload = firstRow.dataBahanB3Id ? { dataBahanB3Id: firstRow.dataBahanB3Id } : { namaProduk: firstRow.namaProduk };

      if (!payload.dataBahanB3Id && !payload.namaProduk) {
        throw new Error("Baris pelaporan ini tidak punya dataBahanB3Id maupun namaProduk");
      }

      await deleteMaterialApi({
        companyId: selectedCompany?.value,
        periodId: selectedPeriod?.value,
        ...payload,
      });

      // Kosongkan pilihan bila bahan yang dihapus sedang dipilih, agar tabel di
      // sebelah kanan tidak menampilkan data yang sudah tidak ada.
      if (selectedMaterial === material?.id) setSelectedMaterial(null);

      await mutate();
    },
    [getRowsForMaterial, mutate, selectedCompany?.value, selectedPeriod?.value, selectedMaterial, setSelectedMaterial],
  );

  return (
    <RootAdmin>
      <Card radius="sm">
        <CardBody>
          <Tabs
            color="primary"
            variant="underlined"
            aria-label="tabs"
            classNames={{
              tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
              cursor: "w-full",
              tab: "max-w-fit px-1.5 h-12",
              tabContent: "group-data-[selected=true]:font-semibold",
            }}
          >
            {isAdminOrPengelola && (
              <Tab key="1" title="Periode Aktif">
                <div className="h-[550px] mt-3">
                  <div className="flex justify-between gap-2 mb-5">
                    <div className="flex gap-2">
                      <div className="flex flex-col justify-end">
                        <ModalAddProductionMaterial setSelectedCompany={setSelectedCompany} setSelectedMaterial={setSelectedMaterial} setSelectedPeriod={setSelectedPeriod} mutate={mutate} />
                      </div>
                    </div>
                    <div className="w-full flex justify-end items-center gap-2">
                      <div className="w-1/4">
                        <FilterReactSelect
                          defaultValue={selectedPeriod}
                          placeholder="Cari Periode"
                          options={dataPeriods?.data?.map((item) => ({ label: item.name, value: item.id }))}
                          isLoading={isLoadingPeriods}
                          setValue={setSelectedPeriod}
                        />
                      </div>
                      <div className="w-1/4">
                        <FilterReactSelect
                          defaultValue={selectedCompany}
                          placeholder="Cari Perusahaan"
                          options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))}
                          isLoading={isLoadingCompany}
                          setValue={setSelectedCompany}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-1 h-[420px]">
                      {!data ? (
                        <div className="h-full flex flex-row items-center justify-center border rounded-md">
                          <h3>Tidak ada data</h3>
                        </div>
                      ) : isLoading ? (
                        <div className="h-full flex flex-row items-center justify-center border rounded-md">
                          <Spinner className="mx-auto" />
                        </div>
                      ) : data?.data?.length === 0 ? (
                        <div className="h-full flex flex-row items-center justify-center border rounded-md">
                          <h3>Tidak ada data</h3>
                        </div>
                      ) : (
                        <ScrollShadow hideScrollBar className="h-full">
                          <RadioGroup value={selectedMaterial} onValueChange={setSelectedMaterial}>
                            {materialData?.map((item) => (
                              <CardRadioButton
                                key={item?.id}
                                value={item?.id}
                                description={item}
                                canManage={isAdminOrPengelola}
                                showEdit={false}
                                deleteWarning="beserta seluruh baris pelaporan produksinya pada periode aktif?"
                                onDelete={handleDeleteMaterial}
                              />
                            ))}
                          </RadioGroup>
                        </ScrollShadow>
                      )}
                    </div>
                    <div className="col-span-3 h-[420px]">
                      <ClientDataGrid
                        columns={columnsTableActivePeriod}
                        rows={selectedMaterial ? filteredData : []}
                        isLoading={isLoading}
                        initialState={{
                          sorting: {
                            sortModel: [{ field: "id", sort: "asc" }],
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Tab>
            )}
            {isAdminOrPengelola && (
              <Tab key="2" title="Preview">
                <TableReportPreview
                  isPreviewOnly
                  selectedCompany={selectedCompany}
                  setSelectedCompany={setSelectedCompany}
                  selectedPeriod={selectedPeriod}
                  setSelectedPeriod={setSelectedPeriod}
                />
              </Tab>
            )}
            <Tab key="3" title="Riwayat">
              <TableHistoryProduction
                dataCompany={dataCompany}
                isLoadingCompany={isLoadingCompany}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                selectedPeriod={selectedPeriod}
                setSelectedPeriod={setSelectedPeriod}
              />
            </Tab>
          </Tabs>
        </CardBody>
      </Card>
    </RootAdmin>
  );
}